import { Box, Typography } from "@mui/material";
import FileCard from "./FileCard.jsx";

/**
 * Renders a wrapping grid of FileCard tiles.
 *
 * Props:
 *   files             — file objects to render (with usedIn as array of { source, label })
 *   selectedIds       — string[]  currently selected IDs
 *   onToggle          — (id: string) => void
 *   multiple          — boolean
 *   emptyText         — string  shown when files is empty (default: hidden)
 *   addDescription    — boolean  allow editing file descriptions
 *   onUpdateDescription — (id: string, description: string) => void
 *   addUsedIn         — boolean  allow tagging source categorization
 *   usedInSuggestions — string[]  autocomplete suggestions
 *   onUpdateUsedIn    — (uuid: string, tags: string[]) => void
 *   unselectableIds   — (string|number)[]  IDs that cannot be selected
 *   showUnselectableAsSelected — boolean  show unselectable files as selected (default: false)
 */
export default function FileGrid({
  files,
  selectedIds,
  onToggle,
  multiple,
  emptyText,
  addDescription = false,
  onUpdateDescription,
  addUsedIn = false,
  usedInSuggestions = [],
  onUpdateUsedIn,
  showUsedInTags = false,
  unselectableIds = [],
  showUnselectableAsSelected = false,
}) {
  if (!files || files.length === 0) {
    return emptyText ? (
      <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
        {emptyText}
      </Typography>
    ) : null;
  }

  return (
    <Box
      component="ul"
      role={multiple ? "group" : "radiogroup"}
      aria-label="File list"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mt: 1,
      }}
    >
      {files.map((file) => {
        const fileId = file.id;
        // Only use unselectableIds prop to determine selectability
        // usedIn is for display purposes only (showing badges), not for blocking selection
        const isUnselectable = unselectableIds.includes(fileId);
        // When showUnselectableAsSelected is true, unselectable files (e.g., linked to costs)
        // are shown as selected since they are effectively pre-selected/pre-present
        const isSelected =
          selectedIds.includes(fileId) ||
          (showUnselectableAsSelected && isUnselectable);
        return (
          <FileCard
            key={file.id}
            file={file}
            selected={isSelected}
            onToggle={onToggle}
            multiple={multiple}
            addDescription={addDescription}
            onUpdateDescription={onUpdateDescription}
            addUsedIn={addUsedIn}
            usedInSuggestions={usedInSuggestions}
            onUpdateUsedIn={onUpdateUsedIn}
            showUsedInTags={showUsedInTags}
            selectable={!isUnselectable}
          />
        );
      })}
    </Box>
  );
}

FileGrid.propTypes = {};
